import React, { useEffect, useReducer, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import openSocket from "../../services/socket-io";

import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { Edit } from "@material-ui/icons";
import AgentModal from "../../components/AgentModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_AGENTS": {
      const newAgents = action.payload;
      const updatedState = [...state];

      newAgents.forEach(agent => {
        const index = updatedState.findIndex(q => q.id === agent.id);
        if (index !== -1) {
          updatedState[index] = agent;
        } else {
          updatedState.push(agent);
        }
      });

      return updatedState;
    }

    case "UPDATE_AGENTS": {
      const updated = action.payload;
      const index = state.findIndex(q => q.id === updated.id);
      if (index !== -1) {
        state[index] = updated;
        return [...state];
      }
      return [updated, ...state];
    }

    case "RESET":
      return [];

    default:
      return state;
  }
};

const Agents = () => {
  const classes = useStyles();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");

  const [agentsquerry, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [agentsModalOpen, setAgentsModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/agentsquerry", { params: { id } });
        dispatch({ type: "LOAD_AGENTS", payload: data });
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("agentsquerry", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_AGENTS", payload: data.agentsquerry });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleOpenCreateModal = () => {
    setSelectedAgent(null);
    setAgentsModalOpen(true);
  };

  const handleEditAgent = (agent) => {
    setSelectedAgent(agent);
    setAgentsModalOpen(true);
  };

  const handleCloseModal = () => {
    setAgentsModalOpen(false);
    setSelectedAgent(null);
  };

  const handleDeleteAgent = async (agentId) => {
    try {
      await api.delete(`/agentsquerry/${agentId}`);
      toast.success(i18n.t("Agents deleted successfully!"));
    } catch (err) {
      toastError(err);
    }
    setSelectedAgent(null);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedAgent &&
          `${i18n.t("agentsquerry.confirmationModal.deleteTitle")} ${selectedAgent.key}?`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteAgent(selectedAgent.id)}
      >
        {i18n.t("agentsquerry.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <AgentModal
        open={agentsModalOpen}
        onClose={handleCloseModal}
        agentId={selectedAgent?.id || null}
        id={id}
      />

      <MainHeader>
        <Title>{i18n.t("agentsquerry.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenCreateModal}
          >
            {i18n.t("agentsquerry.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("agentsquerry.table.key")}</TableCell>
              <TableCell align="center">{i18n.t("agentsquerry.table.value")}</TableCell>
              <TableCell align="center">{i18n.t("agentsquerry.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agentsquerry.map((agent) => (
              <TableRow key={agent.id}>
                <TableCell align="center">{agent.key}</TableCell>
                <TableCell align="center">
                  <div className={classes.customTableCell}>
                    <Typography
                      style={{ width: 300, textAlign: "center" }}
                      noWrap
                      variant="body2"
                    >
                      {agent.value}
                    </Typography>
                  </div>
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => handleEditAgent(agent)}
                  >
                    <Edit />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={3} />}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Agents;
